import { useCallback, useEffect, useState } from 'react';

const SCRIPT_ID = 'google-maps-js-places';

export function loadGoogleMapsPlacesScript(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.places) return Promise.resolve();

  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      const t0 = Date.now();
      const id = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(id);
          resolve();
        } else if (Date.now() - t0 > 20000) {
          clearInterval(id);
          reject(new Error('Google Maps load timeout'));
        }
      }, 50);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    // Per Google: pass loading=async in URL to avoid “loaded directly without loading=async” warning
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&loading=async&v=weekly`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Maps script'));
    document.head.appendChild(script);
  });
}

/**
 * Fetch full Place details (geometry + components) by place_id.
 * Uses a detached div — valid for PlacesService.
 */
export function fetchPlaceDetailsById(placeId) {
  return new Promise((resolve, reject) => {
    if (!placeId || !window.google?.maps?.places) {
      reject(new Error('Maps not ready'));
      return;
    }
    const div = document.createElement('div');
    const service = new window.google.maps.places.PlacesService(div);
    service.getDetails(
      {
        placeId,
        fields: ['address_components', 'geometry', 'formatted_address', 'name'],
      },
      (place, status) => {
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status !== OK || !place) {
          const msg =
            status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED
              ? 'Google denied the request — check API key, billing, and Places API + Maps JavaScript API.'
              : `Could not load place details (${String(status)})`;
          reject(new Error(msg));
          return;
        }
        resolve(place);
      }
    );
  });
}

/**
 * Debounced Places Autocomplete predictions (AutocompleteService).
 * Renders suggestions in your own UI — avoids React/z-index issues with the built-in widget.
 */
export function useGooglePlacesPredictions({ apiKey, searchText, enabled, minChars = 3 }) {
  const [ready, setReady] = useState(() => Boolean(typeof window !== 'undefined' && window.google?.maps?.places));
  const [loadError, setLoadError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [predictionsError, setPredictionsError] = useState(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery((searchText || '').trim()), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (!apiKey) {
      setLoadError(null);
      setReady(false);
      return;
    }
    let cancelled = false;
    setLoadError(null);
    loadGoogleMapsPlacesScript(apiKey)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setReady(false);
          setLoadError(e?.message || 'Google Maps failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !enabled || !apiKey) {
      setPredictions([]);
      setLoading(false);
      return;
    }
    if (debouncedQuery.length < minChars) {
      setPredictions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setPredictionsError(null);
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: debouncedQuery,
        componentRestrictions: { country: 'ca' },
      },
      (results, status) => {
        setLoading(false);
        const places = window.google.maps.places;
        if (status === places.PlacesServiceStatus.ZERO_RESULTS) {
          setPredictions([]);
          return;
        }
        if (status !== places.PlacesServiceStatus.OK || !results) {
          setPredictions([]);
          if (status === places.PlacesServiceStatus.REQUEST_DENIED) {
            setPredictionsError(
              'Google denied autocomplete — enable Places API + Maps JavaScript API, billing, and key restrictions.'
            );
          } else if (status === places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
            setPredictionsError('Places query limit reached — try again later.');
          } else {
            setPredictionsError(`Places: ${String(status)}`);
          }
          return;
        }
        setPredictions(results);
      }
    );
  }, [apiKey, debouncedQuery, enabled, minChars, ready]);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
    setPredictionsError(null);
  }, []);

  return { ready, loadError, predictions, predictionsError, loading, clearPredictions };
}
